import { useMutation } from "react-query";
import usePopup from "../../hooks/usePopup";
import { ResponseType } from "../../Types/ServicesType/servicesType";
import { PatientFullDetailsType } from "../PatientBooking/v2/types";
import { getConstants } from "@utilifiles/methods/storage/storage";
import api from "@services/apiList";
import instance from "@services/services";
const {clinicId} = getConstants()
const { patientRegistrationApi, patientEncounterApi } = api;

export type PatientSearchData = {
  id: number;
  status: number;
  isVip: number;
  foDate: string;
  gender: string;
  age: number;
  dob: string;
  middleName: string | null;
  lastName: string;
  firstName: string;
  mobileNo: string;
  patientId: string;
  emiratesId: string;
};

export type EncounterDetailsByEmrType = {
  encounterId: number;
  createdDate: string;
};

export type BillingLineItemType = {
  billingLineId: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  rate: number;
  totalAmount: number;
  patShare: number;
  insAmount: number;
  insDiscount?: number;
};

export type BillingPayDetailType = {
  billNo: number;
  encounterId: number;
  doctorName: string;
  date: string;
  totalCptAmount: number;
  patientShareAmount: number;
  insuranceAmount: number;
  balanceAmount: number;
  amountPayed: number;
  allBillsByResponseDto: BillingLineItemType[];
};

export type BillingDateGroupType = {
  date: string;
  totalCptAmount: number;
  patientShareAmount: number;
  patientBalanceAmount?: number;
  totalInsuranceAmount: number;
  totalAmountPayed: number;
  totalItemWiseDiscount?: number;
  totalDiscountAmount?: number;
  paydetails: BillingPayDetailType[];
};

export function getBillingDateBalanceAmount(group: BillingDateGroupType): number {
  return (group.paydetails ?? []).reduce(
    (sum, bill) => sum + Number(bill.balanceAmount ?? 0),
    0,
  );
}

export type BillingHistoryPayloadType = {
  totalItemWiseDiscount: number;
  totalDiscountAmount: number;
  totalCptAmount: number;
  grantTotalPatShare: number;
  grantTotalInsuranceAmount: number;
  grandTotalDiscountamount?: number;
  grandTotalItemWiseDiscount?: number;
  dates: BillingDateGroupType[];
};

type ParamsType = {
  encounterId?: number | null;
  emrNo?: string;
  bookingId?: number | null;
  emiratesId?: string | null;
  gccId?: string | null;
  passNum?: string | null;
};
export const useFetchPatientDetailsByEMR = ({
  onSuccess,
}: {
  onSuccess?: (data: ResponseType<PatientFullDetailsType>) => void;
}) => {
  const { showSnackbar } = usePopup();
  return useMutation(
    async (
      params: ParamsType
    ): Promise<ResponseType<PatientFullDetailsType>> => {
      if (Boolean(params.emiratesId || params.gccId || params.passNum)) {
        return (
          await instance.post(`${patientRegistrationApi.getPatientById}`, {
            clinicId,
            ...params,
          })
        ).data;
      } else {
        return (
          await instance.post(
            `${patientRegistrationApi.getPatientByemr}`,
            params.encounterId
              ? { clinicId, ...params, encounterId: params.encounterId }
              : { clinicId, ...params, encounterId: params.encounterId || null }
          )
        ).data;
      }
    },
    {
      onSuccess: (data) => {
        if (onSuccess) {
          onSuccess(data);
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    }
  );
};

export const usePatientSearch = () => {
  return useMutation(
    async ({
      searchText,
    }: {
      searchText: string;
    }): Promise<ResponseType<PatientSearchData[]>> => {
      return (
        await instance.post(`${patientEncounterApi.getAllPatient}`, {
          emrNo: searchText,
          clinicId: clinicId,
        })
      ).data;
    }
  );
};

export const useFetchEncounterDetailsByEmr = () => {
  const { showSnackbar } = usePopup();
  return useMutation(
    async (
      emrNo: string
    ): Promise<ResponseType<EncounterDetailsByEmrType[]> | EncounterDetailsByEmrType[]> => {
      return (
        await instance.get(`${patientEncounterApi.getEncounterDetailsByEmr}/${emrNo}`)
      ).data;
    },
    {
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Unable to load encounter details",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    }
  );
};

export const useFetchBillingHistoryByEmrNo = () => {
  const { showSnackbar } = usePopup();
  return useMutation(
    async (emrNo: string): Promise<ResponseType<BillingHistoryPayloadType>> => {
      const { user } = getConstants();
      const isQatarRegion = user?.codetype.toLowerCase() === "internal";
      const billingHistoryApi = isQatarRegion
        ? patientEncounterApi.getBillingHistoryByEncounterIdInternalCodes
        : patientEncounterApi.getBillingHistoryByEncounterId;
      return (
        await instance.get(
          `${billingHistoryApi}/${emrNo}`
        )
      ).data;
    },
    {
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Unable to load billing history",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    }
  );
};
